import { recalcItem } from './orderItemCalc';
import type { FulfillmentMode, OrderItem } from '../types';

/**
 * TCR-2614 — "Tách dòng": cho phép 1 SKU xuất hiện ở 2 dòng cùng kho nhưng khác
 * giá/SL (vd 160 cái giá 295.000 + 5 cái hàng tặng giá 0).
 *
 * Trước đây thêm lại cùng SKU luôn cộng dồn vào dòng cũ, `mergeLooseDuplicates`
 * cũng gộp các dòng cùng variant+kho+giá, nên không có cách nào giữ 2 dòng.
 */

/** Dòng nào được phép tách: chỉ dòng lẻ thường (không combo/flash/serial/quà/dòng đã tách). */
export function canSplitLine(item: OrderItem): boolean {
  return (
    item.trackingMode === 'quantity' &&
    !item.comboGroupId &&
    !item.specificationCode &&
    !item.is_gift &&
    !item.flash_sale_id &&
    !item.flash_sale_item_id &&
    !item.flashOverflowOf &&
    !item.splitLine
  );
}

/**
 * Chèn NGAY SAU dòng `id` một dòng mới cùng biến thể/kho/đơn vị, SL 1, giá 0.
 *
 * - `splitLine: true` là cờ bảo vệ RIÊNG (không dựa vào `hasPriceOverride`, vì cờ này bị
 *   reset khi đổi khách / bỏ khách B2B và bị tính lại mỗi lần gõ giá) — dòng tách không bị
 *   gộp ngược, không bị cộng dồn khi thêm SKU, giá 0 không bị bảng giá B2B ghi đè.
 * - Bỏ `lineId`/`savedSku`/`splitComboGroupId`: đây là dòng MỚI chưa lưu; mang lineId của
 *   dòng cũ sang thì BE coi 1 dòng đã lưu bị tham chiếu 2 lần.
 * - Bỏ `policyDiscount`/`overrideReason`/CK: để màn sửa đơn resolve lại chính sách cho dòng mới.
 * Trả về chính `items` nếu dòng không tồn tại / không tách được.
 */
export function splitItemLine(
  items: OrderItem[],
  id: string,
  newId: () => string,
  fulfillmentMode: FulfillmentMode,
): OrderItem[] {
  const index = items.findIndex((i) => i.id === id);
  if (index < 0 || !canSplitLine(items[index])) return items;
  const source = items[index];
  const line = recalcItem(
    {
      ...source,
      id: newId(),
      lineId: undefined,
      savedSku: undefined,
      splitComboGroupId: undefined,
      overrideReason: undefined,
      policyDiscount: undefined,
      sellerStaffId: source.sellerStaffId,
      quantity: 1,
      price: 0,
      discountType: 'percent',
      discountPercent: 0,
      discountAmount: 0,
      hasDiscountOverride: false,
      hasPriceOverride: true,
      splitLine: true,
    },
    fulfillmentMode,
  );
  return [...items.slice(0, index + 1), line, ...items.slice(index + 1)];
}

/**
 * Tổng SL của các dòng KHÁC cùng biến thể + cùng kho — để cảnh báo tồn kho tính theo
 * tổng thực xuất, không theo riêng từng dòng (2 dòng cùng SKU cùng trừ một kho).
 */
export function siblingQuantity(items: OrderItem[], item: OrderItem): number {
  return items
    .filter(
      (i) =>
        i.id !== item.id &&
        i.variantId === item.variantId &&
        (i.warehouseId ?? '') === (item.warehouseId ?? ''),
    )
    .reduce((sum, i) => sum + i.quantity, 0);
}
